/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.19.0
 * @since 1.0.0
 */

import { createRef, PureComponent, RefObject } from 'react';

import moment, { Moment } from 'moment';
import 'react-input-range/lib/css/index.css';

import ICommonTranslationCalendar from '../../../modules/internationalization/types/common/calendar';
import PickerUI from './PickerUI';
import { UnlinkedCalendar } from './UnlinkedCalendar';

export interface IRangeDatePicker {
    className?: string;
    endDate?: Moment;
    hideTime?: boolean;
    locale?: object;
    maxDate?: Moment;
    maxYear?: string;
    messages: ICommonTranslationCalendar;
    minDate?: Moment;
    minYear?: string;
    onApply: (leftRange: Moment, rightRange: Moment) => void;
    range?: Moment;
    selectComponentProps?: object;
    showDropdown?: boolean;
    single?: boolean;
    startDate?: Moment;
    timeLeftHour?: number;
    timeLeftMin?: number;
    timePicker?: boolean;
    timeRightHour?: number;
    timeRightMin?: number;
    weekStart?: number;
}

/* Set default props */
const calendar = moment();
const getDefaultMinYear = (): string => {
    return calendar
        .clone()
        .subtract(5, 'year')
        .format('YYYY');
};

const getDefaultMaxYear = (): string => {
    return calendar
        .clone()
        .add(6, 'year')
        .format('YYYY');
};

class RangeDatePicker extends PureComponent<IRangeDatePicker> {

    getYearNames = (): number[] => {
        let sY = +(this.props.minYear || getDefaultMinYear());
        const eY = +(this.props.maxYear || getDefaultMaxYear());
        const yearNames = [];
        for (; sY <= eY; sY++) {
            yearNames.push(sY);
        }
        return yearNames;
    };

    public static defaultProps = {
        endDate: calendar.clone().endOf('month'),
        maxYear: getDefaultMaxYear(),
        minYear: getDefaultMinYear(),
        range: calendar.clone().add(1, 'month'),
        selectComponentProps: {
            kind: 'normal',
        },
        showDropdown: true,
        startDate: calendar.clone().startOf('month'),
        timeLeftHour: 0,
        timeLeftMin: 0,
        timePicker: true,
        timeRightHour: 23,
        timeRightMin: 55,
        weekStar: 1,
    };

    public calendarRef = createRef<PickerUI>();

    public onApply = () => {
        const picker: RefObject<PickerUI> = this.calendarRef;

        if (picker.current) {
            const startDate: Moment = picker.current.state.startDate;
            const endDate: Moment = picker.current.state.endDate;
            const stateUnlinkedCalendar = picker.current.getTimeRange();
            // set time props
            startDate.set({
                hour: stateUnlinkedCalendar.timeLeftHour,
                millisecond: 0,
                minute: stateUnlinkedCalendar.timeLeftMin,
                seconds: 0,
            });
            endDate.set({
                hour: stateUnlinkedCalendar.timeRightHour,
                millisecond: 0,
                minute: stateUnlinkedCalendar.timeRightMin,
                seconds: 0,
            });

            this.props.onApply(
                moment(startDate),
                moment(endDate),
            );
        }
    };

    public render() {
        const { weeks, months, weekLabel } = this.props.messages;
        const locale = {
            monthNames: months,
            weekLabel,
            weekNames: weeks,
            yearNames: this.getYearNames(),
        };
        return (
            <PickerUI {...this.props}
                      className="range-date-picker__picker-ui"
                      component={UnlinkedCalendar}
                      locale={locale}
                      ref={this.calendarRef}
            />
        );
    }
}

export default RangeDatePicker;
